<template>
  <v-card
    :class="cardClasses"
    :elevation="elevation"
    :outlined="outlined"
    :tile="tile"
    :hover="hover"
    v-bind="$attrs"
  >
    <!-- Card Header -->
    <v-card-title
      v-if="title || $slots.title"
      :class="titleClasses"
    >
      <v-icon
        v-if="titleIcon"
        left
        :color="titleIconColor"
      >
        {{ titleIcon }}
      </v-icon>

      <span v-if="title">{{ title }}</span>
      <slot v-else name="title" />

      <v-spacer v-if="showActions || $slots.actions" />

      <div v-if="showActions || $slots.actions" class="card-actions">
        <slot name="actions">
          <v-btn
            v-if="showRefresh"
            icon
            small
            :loading="refreshing"
            @click="onRefresh"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            v-if="showExpand"
            icon
            small
            @click="toggleExpanded"
          >
            <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>

          <v-btn
            v-if="showClose"
            icon
            small
            @click="onClose"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </slot>
      </div>
    </v-card-title>

    <!-- Card Subtitle -->
    <v-card-subtitle
      v-if="subtitle || $slots.subtitle"
      :class="subtitleClasses"
    >
      <span v-if="subtitle">{{ subtitle }}</span>
      <slot v-else name="subtitle" />
    </v-card-subtitle>

    <!-- Card Content -->
    <v-expand-transition>
      <div v-show="!collapsible || expanded">
        <v-card-text
          v-if="$slots.default"
          :class="contentClasses"
        >
          <slot />
        </v-card-text>

        <!-- Card Actions -->
        <v-card-actions
          v-if="$slots.cardActions"
          :class="actionsClasses"
        >
          <slot name="cardActions" />
        </v-card-actions>
      </div>
    </v-expand-transition>

    <!-- Loading Overlay -->
    <v-overlay
      v-if="loading"
      absolute
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-overlay>
  </v-card>
</template>

<script>
export default {
  name: 'ResponsiveCard',

  props: {
    // Content props
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    titleIcon: {
      type: String,
      default: '',
    },
    titleIconColor: {
      type: String,
      default: 'primary',
    },

    // Visual props
    elevation: {
      type: [Number, String],
      default: 1,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    tile: {
      type: Boolean,
      default: false,
    },
    hover: {
      type: Boolean,
      default: false,
    },

    // Responsive props
    mobileFullWidth: {
      type: Boolean,
      default: true,
    },
    tabletPadding: {
      type: Boolean,
      default: true,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },

    // Behavior props
    collapsible: {
      type: Boolean,
      default: false,
    },
    defaultExpanded: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },

    // Action props
    showRefresh: {
      type: Boolean,
      default: false,
    },
    showExpand: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    refreshing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      expanded: this.defaultExpanded,
    };
  },

  computed: {
    cardClasses() {
      return {
        'responsive-card': true,
        'mobile-full-width': this.mobileFullWidth,
        'tablet-padding': this.tabletPadding,
      };
    },

    titleClasses() {
      return {
        'pb-2': true,
        'text-h6': this.$vuetify.breakpoint.smAndUp,
        'text-subtitle-1': this.$vuetify.breakpoint.xs,
      };
    },

    subtitleClasses() {
      return {
        'pt-0': true,
        'pb-2': true,
      };
    },

    contentClasses() {
      return {
        'pt-0': !!(this.title || this.$slots.title || this.subtitle || this.$slots.subtitle),
        'pa-0': this.noPadding,
      };
    },

    actionsClasses() {
      return {
        'pt-0': true,
      };
    },

    showActions() {
      return this.showRefresh || this.showExpand || this.showClose;
    },
  },

  watch: {
    collapsible(newVal) {
      if (newVal && this.showExpand === false) {
        this.showExpand = true;
      }
    },
  },

  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
      this.$emit('toggle-expanded', this.expanded);
    },

    onRefresh() {
      this.$emit('refresh');
    },

    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.responsive-card {
  transition: all 0.3s ease;
}

.mobile-full-width {
  width: 100%;
}

@media (max-width: 600px) {
  .mobile-full-width {
    margin: 0 !important;
    border-radius: 0 !important;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .tablet-padding .v-card__text {
    padding: 20px !important;
  }
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
